import { useTranslation } from 'next-i18next';
import { Tabs } from '@hub/common/Tabs/Tabs';
import { getLocale, IbhFilterKeys } from '@hub/libs/constants';
import React, { useEffect, useState } from 'react';
import { Dialog } from '@hub/common/Dialog/Dialog';
import { useRouter } from 'next/router';
import type {
  IbhMaterialIndustriesDto,
  IbhMaterialResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api';
import { getProductionMethodFromProcedureText } from '@hub/bearing-hub/common/utils';
import { isMobile } from 'react-device-detect';

export interface CompareMaterialsModalProps {
  activeModal: boolean;
  materials: IbhMaterialResponseDto[];
  parts: IbhPartsResponseDto[];

  setActiveModal: (value: boolean) => void;
  setFilterMaterial: (filterKey: IbhFilterKeys.MATERIAL, materialIdList: string[]) => void;
}

type TableData = {
  key: keyof Omit<IbhMaterialResponseDto, 'industries'> | keyof IbhMaterialIndustriesDto;
  translationKey: string;
}[];

enum TableDataType {
  CHARACTERISTIC = 'characteristic',
  INDUSTRY = 'industry',
}

const presentedMaterialCharacteristic: TableData = [
  {
    key: 'dirtResistant',
    translationKey: 'DIRT_RESISTANT',
  },
  {
    key: 'highChemicalResistance',
    translationKey: 'HIGH_CHEMICAL_RESISTANCE',
  },
  {
    key: 'foodContactFDACompliant',
    translationKey: 'FOOD_CONTACT_FDA_COMPLIANT',
  },
  {
    key: 'foodContactEUCompliant',
    translationKey: 'FOOD_CONTACT_EU_COMPLIANT',
  },
  {
    key: 'lowWaterAbsorption',
    translationKey: 'LOW_WATER_ABSORPTION',
  },
  {
    key: 'underWaterCompliant',
    translationKey: 'UNDER_WATER_COMPLIANT',
  },
  {
    key: 'mouldFree',
    translationKey: 'MOULD_FREE',
  },
  {
    key: 'rohsCompliance',
    translationKey: 'ROHS_COMPLIANCE',
  },
  {
    key: 'detectable',
    translationKey: 'DETECTABLE',
  },
  {
    key: 'vibrationDampening',
    translationKey: 'VIBRATION_DAMPENING',
  },
  {
    key: 'lowCoefficientsOfFriction',
    translationKey: 'LOW_COEFFICIENTS_OF_FRICTION',
  },
  {
    key: 'maximumHoldingTimesInDryOperation',
    translationKey: 'MAXIMUM_HOLDING_TIMES_IN_DRY_OPERATION',
  },
  {
    key: 'PTFEFree',
    translationKey: 'PTFE_FREE',
  },
  {
    key: 'resistantToEdgeLoads',
    translationKey: 'RESISTANT_TO_EDGE_LOADS',
  },
  {
    key: 'electricallyConductive',
    translationKey: 'ELECTRICALLY_CONDUCTIVE',
  },
];
const presentedIndustryBranches: TableData = [
  {
    key: 'agriculturalEconomy',
    translationKey: 'AGRICULTURAL_ECONOMY',
  },
  {
    key: 'automotive',
    translationKey: 'AUTOMOTIVE',
  },
  {
    key: 'aviation',
    translationKey: 'AVIATION',
  },
  {
    key: 'beverageIndustry',
    translationKey: 'BEVERAGE_INDUSTRY',
  },
  {
    key: 'constructionMachinery',
    translationKey: 'CONSTRUCTION_MACHINERY',
  },
  {
    key: 'fluidTechnology',
    translationKey: 'FLUID_TECHNOLOGY',
  },
  {
    key: 'foodIndustry',
    translationKey: 'FOOD_INDUSTRY',
  },
  {
    key: 'furnitureIndustrialDesign',
    translationKey: 'FURNITURE_INDUSTRIAL_DESIGN',
  },
  {
    key: 'machineTools',
    translationKey: 'MACHINE_TOOLS',
  },
  {
    key: 'medicalTechnology',
    translationKey: 'MEDICAL_TECHNOLOGY',
  },
  {
    key: 'packagingIndustry',
    translationKey: 'PACKAGING_INDUSTRY',
  },
  {
    key: 'railwayEngineering',
    translationKey: 'RAILWAY_ENGINEERING',
  },
  {
    key: 'renewableEnergies',
    translationKey: 'RENEWABLE_ENERGIES',
  },
  {
    key: 'twoWheeler',
    translationKey: 'TWO_WHEELER',
  },
];
export const CompareMaterialsModal = ({
  activeModal,
  setActiveModal,
  materials,
  parts,
  setFilterMaterial,
}: CompareMaterialsModalProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const router = useRouter();
  const locale = getLocale(router);

  useEffect(() => {
    if (isMobile) {
      setIsFullScreen(true);
    }
  }, []);

  const getMaterialsToDisplay = () => {
    const materialsToDisplayId = [...new Set(parts.map(part => part.material.id))];
    return materials.filter(material => materialsToDisplayId.includes(material.id));
  };

  const canRenderDot = (
    dataType: TableDataType,
    material: IbhMaterialResponseDto,
    key: keyof Omit<IbhMaterialResponseDto, 'industries'> | IbhMaterialIndustriesDto
  ): boolean => {
    if (dataType === TableDataType.INDUSTRY && material.industries) {
      return material.industries[key as keyof IbhMaterialIndustriesDto];
    }
    return material[key as keyof IbhMaterialResponseDto] as boolean;
  };
  const [maxTableHeight, setMaxTableHeight] = useState<number>();

  const onResize = () => {
    const tabsPane = window.document.querySelectorAll('[data-testid="tabs-pane"]')?.[0];
    const tabsContainer = window.document.querySelectorAll('[data-testid="dialog-container"]')?.[0];
    if (tabsPane && tabsContainer) {
      setMaxTableHeight(tabsContainer.clientHeight - tabsPane.getBoundingClientRect().top - 30);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => onResize(), [isFullScreen]);

  const table = (tableData: TableData, dataType: TableDataType) => (
    <table>
      <thead>
        <th className="min-w-[130px] md:min-w-[180px] sticky top-0 left-0 z-20 bg-white" />
        {getMaterialsToDisplay().map((material, index) => (
          <th
            key={material.id}
            className={`min-w-[120px] px-2 whitespace-nowrap ${
              index % 2 === 0 ? 'bg-gray-50' : ''
            } sticky top-0 z-10 bg-white`}
          >
            <p className="text-sm font-bold pt-2">{material.name}</p>
            <p className="text-sm font-normal">{getProductionMethodFromProcedureText(material.procedure, t)}</p>
            <button
              type="button"
              className="uppercase text-igus-primary font-medium py-6"
              data-testid={`select_material_${material.id}`}
              onClick={() => {
                setFilterMaterial(IbhFilterKeys.MATERIAL, [material.id]);
                setActiveModal(false);
              }}
            >
              {t(`COMPARE_MATERIALS_MODAL.SELECT`, { ns: 'bearing-hub/bearingHub' })}
            </button>
          </th>
        ))}
      </thead>
      <tbody>
        {tableData.map(data => (
          <tr key={data.key} className="border-t border-[#AFAFAF]">
            <td className="px-4 text-sm sticky left-0 z-10 bg-white">
              {t(`COMPARE_MATERIALS_MODAL.${data.translationKey}`, { ns: 'bearing-hub/bearingHub' })}
            </td>
            {getMaterialsToDisplay().map((material, index) => (
              <td key={material.id} className={`h-[56px] ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
                {canRenderDot(
                  dataType,
                  material,
                  data.key as keyof Omit<IbhMaterialResponseDto, 'industries'> | IbhMaterialIndustriesDto
                ) && (
                  <span
                    className="block w-[12px] h-[12px] bg-gray-800 rounded-full m-auto"
                    data-testid={`${material.id}_${data.key as string}_dot`}
                  />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  const tabs = [
    {
      title: t(`COMPARE_MATERIALS_MODAL.TAB_MATERIAL_CHARACTERISTICS`, { ns: 'bearing-hub/bearingHub' }),
      content: (
        <div style={{ maxHeight: `${isFullScreen ? maxTableHeight : 450}px` }} className="overflow-auto">
          {table(presentedMaterialCharacteristic, TableDataType.CHARACTERISTIC)}
        </div>
      ),
    },
    {
      title: t(`COMPARE_MATERIALS_MODAL.TAB_INDUSTRY_BRANCHES`, { ns: 'bearing-hub/bearingHub' }),
      content: (
        <div style={{ maxHeight: `${isFullScreen ? maxTableHeight : 450}px` }} className="overflow-auto">
          {table(presentedIndustryBranches, TableDataType.INDUSTRY)}
        </div>
      ),
    },
  ];

  return (
    <Dialog
      closable
      fullScreen
      visible={activeModal}
      onClose={() => {
        setActiveModal(false);
      }}
      onSetFullScreen={setIsFullScreen}
    >
      {locale === 'en-US' ? (
        <iframe
          data-testid="compare-materials-iframe"
          title="Compare Materials"
          className={`${isFullScreen ? 'w-full h-full' : 'w-[50vw] h-[70vh]'}`}
          allowFullScreen
          src="https://embed.figma.com/proto/BCaTq1wVUK3aLnyATLd2G0?embed-host=iglide-us&content-scaling=fixed&kind=proto&node-id=3-3&page-id=0%3A1&scaling=scale-down&starting-point-node-id=3%3A3"
        />
      ) : (
        <div className={`${isFullScreen ? 'w-[100vw] h-[100vh]' : 'w-[50vw]'} pt-16 px-8`}>
          <h3 data-testId="life-Time-Modal_Header">
            {t('HEADLINES.COMPARE_MATERIALS_MODAL', { ns: 'bearing-hub/bearingHub' })}
          </h3>
          <p>
            {t(`COMPARE_MATERIALS_MODAL.DESCRIPTION`, {
              ns: 'bearing-hub/bearingHub',
              quantity: getMaterialsToDisplay().length,
            })}
          </p>
          <div className={!isFullScreen ? 'mb-8' : ''}>
            <Tabs items={tabs} headerWidth="auto" />
          </div>
        </div>
      )}
    </Dialog>
  );
};
